import {
  useNavigation,
  useNavigationState,
} from '../../navigation/NavigationProvider';
import { AddIcon, EditIcon } from '../../../components/ui/icons';
import { recordKey } from '../../collection/multiCollectionIdentity';
import { ReactNode, useEffect, useId, useRef } from 'react';
import {
  Box,
  FormHelperText,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { Combobox } from '../../../components/ui/Combobox';
import { Location } from '../../../domain/location';
import { LocationEditorDialog, LocationUpdate } from './LocationEditorDialog';
import { locationLabel, locationOptions } from './locationOptions';

export function LocationPicker({
  navigationKey,
  value,
  locations,
  onChange,
  onAdd,
  onSaveLocation,
  onDeleteLocation,
  label = 'Storage location',
  helperText,
  sx,
  readOnly = false,
  canManageLocations = true,
  loading = false,
  currentUserId,
  currentUsername,
  collectionId,
  membershipVersion,
}: {
  navigationKey?: string;
  value: string;
  locations: Location[];
  onChange: (value: string) => void;
  onAdd: () => void;
  onSaveLocation: (update: LocationUpdate) => Promise<boolean>;
  onDeleteLocation: (location: Location) => Promise<boolean>;
  label?: string;
  helperText?: ReactNode;
  sx?: object;
  readOnly?: boolean;
  canManageLocations?: boolean;
  loading?: boolean;
  currentUserId?: string;
  currentUsername?: string;
  collectionId?: string;
  membershipVersion?: number;
}) {
  const instanceId = useId();
  const scopeKey = navigationKey || `location-picker:${instanceId}`;
  const { replaceState } = useNavigation();
  const [editingKey, setEditingKey] = useNavigationState<string | null>(
    scopeKey,
    null,
  );
  const editing =
    locations.find((location) => recordKey(location) === editingKey) || null;
  useEffect(() => {
    if (editingKey && !editing) replaceState(scopeKey, null);
  }, [editingKey, editing, replaceState, scopeKey]);
  const editButton = useRef<HTMLButtonElement>(null);
  const selected =
    locations.find(
      (location) =>
        recordKey(location) === value ||
        (!location.collectionId && location.name === value),
    ) || null;
  const selectedOption =
    selected ||
    (value
      ? {
          id: value,
          name: value,
          normalizedName: value,
          description: '',
          createdAt: '',
        }
      : null);
  const grouped = locationOptions(
    selectedOption && !selected ? [selectedOption, ...locations] : locations,
  );
  const id = `location-picker-${label.toLocaleLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  const description = selected?.description.trim() ? selected.description : '';
  const describedBy =
    [description && `${id}-description`, helperText && `${id}-helper`]
      .filter(Boolean)
      .join(' ') || undefined;

  const closeEditor = () => {
    setEditingKey(null);
    window.setTimeout(() => editButton.current?.focus(), 250);
  };
  return (
    <Box sx={{ minWidth: 0, ...sx }}>
      <Box
        sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, minWidth: 0 }}
      >
        <Combobox<Location>
          sx={{ flex: 1, minWidth: 0 }}
          id={id}
          label={label}
          size="small"
          placeholder="Filter locations…"
          autoFocusSearch={false}
          describedBy={describedBy}
          disabled={readOnly}
          loading={loading}
          value={selectedOption}
          {...grouped}
          getOptionLabel={locationLabel}
          getOptionText={(option) => option.name}
          getOptionKey={recordKey}
          getOptionDisabled={(option) => option === selectedOption && !selected}
          onChange={(location) =>
            onChange(
              location.collectionId ? recordKey(location) : location.name,
            )
          }
        />
        {!readOnly && canManageLocations && (
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            <Tooltip title="Edit location">
              <span>
                <IconButton
                  ref={editButton}
                  aria-label="Edit location"
                  disabled={
                    !selected ||
                    Boolean(
                      collectionId &&
                      selected.collectionId &&
                      selected.collectionId !== collectionId,
                    )
                  }
                  onClick={() => selected && setEditingKey(recordKey(selected))}
                  sx={{
                    width: 44,
                    height: 44,
                    border: 0,
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    color: 'text.secondary',
                  }}
                >
                  <EditIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Add location">
              <IconButton
                aria-label="Add location"
                onClick={onAdd}
                sx={{
                  width: 44,
                  height: 44,
                  border: 0,
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  color: 'text.secondary',
                }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
      {description && (
        <Typography
          id={`${id}-description`}
          variant="body2"
          color="text.secondary"
          sx={{
            mx: 1.75,
            mt: 1,
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
          }}
        >
          {description}
        </Typography>
      )}
      {helperText && (
        <FormHelperText
          id={`${id}-helper`}
          sx={{ mx: 1.75, mt: 1, overflowWrap: 'anywhere' }}
        >
          {helperText}
        </FormHelperText>
      )}
      <LocationEditorDialog
        scopeKey={scopeKey}
        open={Boolean(editing)}
        location={editing}
        readOnly={readOnly}
        onClose={closeEditor}
        onSave={async (update) => {
          const succeeded = await onSaveLocation(update);
          if (succeeded)
            onChange(editing?.collectionId ? recordKey(editing) : update.name);
          return succeeded;
        }}
        onDelete={async (location) => {
          const succeeded = await onDeleteLocation(location);
          if (succeeded) onChange('');
          return succeeded;
        }}
        currentUserId={currentUserId}
        currentUsername={currentUsername}
        collectionId={collectionId}
        membershipVersion={membershipVersion}
      />
    </Box>
  );
}
